import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Switch,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import "../../styles/popupStyle.css";
import { removeSampleTestArray } from "../../actions/cleanup";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import {
  validateEmail,
  validatePhoneNumber,
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveUserDetailsFieldErrors,
  reserveUserDetailsFieldHelperText,
} from "../../../constants/reserveValidationHelperText";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { RESERVE_OUTLET_FIELD_OUTLET_NAME } from "../../../constants/ReserveOutletSetupFields";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import { textStyle } from "../../styles/Style";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveListAvailableHeadingTooltip,
  reserveRowFieldTooltip,
  reserveUserDetailsTooltip,
} from "../../../constants/ReserveTooltipInformation";

const COUNTRY_CODE_MAX_DIGITS = 4;

const clearAllFieldErrors = (setters: {
  setFirstNameErr: (v: boolean) => void;
  setLastNameErr: (v: boolean) => void;
  setCountryCodeErr: (v: boolean) => void;
  setMobileErr: (v: boolean) => void;
  setEmailErr: (v: boolean) => void;
  setEmployeeCodeErr: (v: boolean) => void;
  setUsernameErr: (v: boolean) => void;
  setPasswordErr: (v: boolean) => void;
  setPinErr: (v: boolean) => void;
  setRoleErr: (v: boolean) => void;
}) => {
  setters.setFirstNameErr(false);
  setters.setLastNameErr(false);
  setters.setCountryCodeErr(false);
  setters.setMobileErr(false);
  setters.setEmailErr(false);
  setters.setEmployeeCodeErr(false);
  setters.setUsernameErr(false);
  setters.setPasswordErr(false);
  setters.setPinErr(false);
  setters.setRoleErr(false);
};

const ReserveUserDetails = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("");
  const [mobile, setMobile] = useState<string>("");
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [employeeCode, setEmployeeCode] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [pin, setPin] = useState<string>("");
  const [retailRole, setRetailRole] = useState<boolean>(false);
  const [roleCode, setRoleCode] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [firstNameErr, setFirstNameErr] = useState<boolean>(false);
  const [lastNameErr, setLastNameErr] = useState<boolean>(false);
  const [countryCodeErr, setCountryCodeErr] = useState<boolean>(false);
  const [mobileErr, setMobileErr] = useState<boolean>(false);
  const [emailErr, setEmailErr] = useState<boolean>(false);
  const [employeeCodeErr, setEmployeeCodeErr] = useState<boolean>(false);
  const [usernameErr, setUsernameErr] = useState<boolean>(false);
  const [passwordErr, setPasswordErr] = useState<boolean>(false);
  const [pinErr, setPinErr] = useState<boolean>(false);
  const [roleErr, setRoleErr] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [userDetailsInformation, setUserDetailsInformation] = useState<any[]>(
    [],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [rolesList, setRolesList] = useState<any>([]);
  const [outletAccess, setOutletAccess] = useState<string[]>([]);
  const [outletAccessList, setOutletAccessList] = useState<any>([]);

  const validateInput = (): boolean => {
    clearAllFieldErrors({
      setFirstNameErr,
      setLastNameErr,
      setCountryCodeErr,
      setMobileErr,
      setEmailErr,
      setEmployeeCodeErr,
      setUsernameErr,
      setPasswordErr,
      setPinErr,
      setRoleErr,
    });

    let valid = true;
    const fn = firstName.trim();
    const ln = lastName.trim();

    if (
      fn &&
      !validateString(fn, validationLength.firstName, validationType.SPACE)
    ) {
      setFirstNameErr(true);
      valid = false;
    }
    if (
      ln &&
      !validateString(ln, validationLength.lastName, validationType.SPACE)
    ) {
      setLastNameErr(true);
      valid = false;
    }

    const cc = countryCode.trim();
    if (cc && (!/^\d+$/.test(cc) || cc.length > COUNTRY_CODE_MAX_DIGITS)) {
      setCountryCodeErr(true);
      valid = false;
    }

    if (mobile.trim() && !validatePhoneNumber(mobile.trim())) {
      setMobileErr(true);
      valid = false;
    }

    if (!emailAddress.trim()) {
      setEmailErr(true);
      valid = false;
    } else if (!validateEmail(emailAddress.trim())) {
      setEmailErr(true);
      valid = false;
    }

    if (!employeeCode.trim()) {
      setEmployeeCodeErr(true);
      valid = false;
    } else if (
      !validateString(
        employeeCode.trim(),
        validationLength.roomFeatureName,
        validationType.NAME,
      )
    ) {
      setEmployeeCodeErr(true);
      valid = false;
    }

    if (!username.trim()) {
      setUsernameErr(true);
      valid = false;
    } else if (
      !validateString(
        username.trim(),
        validationLength.roomFeatureName,
        validationType.NAME,
      )
    ) {
      setUsernameErr(true);
      valid = false;
    }

    if (!password.trim()) {
      setPasswordErr(true);
      valid = false;
    } else if (
      !validateString(
        password.trim(),
        validationLength.retailUserSetupPassword,
        validationType.NAME,
      )
    ) {
      setPasswordErr(true);
      valid = false;
    }

    const pinValue = pin.trim();
    if (
      pinValue &&
      !validateString(
        pinValue,
        validationLength.retailUserSetupPassword,
        validationType.NAME,
      )
    ) {
      setPinErr(true);
      valid = false;
    }

    if (!roleCode.trim()) {
      setRoleErr(true);
      valid = false;
    }

    return valid;
  };

  useEffect(() => {
    if (fieldValues) {
      const list = fieldValues[ReservePropertyEnum.userDetails];
      setUserDetailsInformation(Array.isArray(list) ? list : []);
      const raw = fieldValues[ReservePropertyEnum.roles];
      setRolesList(
        Array.isArray(raw)
          ? raw.filter((r: any) => r && r["Role Code"] && r.Active !== false)
          : [],
      );
      setOutletAccessList(
        fillAutocompleteValues(
          fieldValues[ReservePropertyEnum.outletSetup],
          RESERVE_OUTLET_FIELD_OUTLET_NAME as DNDFieldsEnum,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(userDetailsInformation);
    editClicked &&
      setSelectedId(
        checkData(userDetailsInformation, selectedId) ? selectedId : null,
      );
  }, [userDetailsInformation]);

  const payload = {
    Name: `${firstName.trim()} ${lastName.trim()}`.trim(),
    "First Name": firstName.trim(),
    "Last Name": lastName.trim(),
    "Country Code": countryCode.trim(),
    Mobile: mobile.trim(),
    "Email address": emailAddress.trim(),
    "Employee code": employeeCode.trim(),
    Username: username.trim(),
    Password: password.trim(),
    PIN: pin.trim(),
    "Retail role": retailRole,
    "Role Code": roleCode,
    "Role Name":
      (rolesList.find(
        (r: any) => String(r["Role Code"]) === String(roleCode),
      )?.["Role Name"] as string | undefined) ?? "",
    "Outlet Access": outletAccess.join(", "),
    Active: active,
  };

  const updateReserveUserDetails = () => {
    if (!validateInput()) return;
    const others = (userDetailsInformation ?? []).filter(
      (item: any) => String(item.id) !== String(selectedId),
    );
    if (checkDuplicate(others, "Employee code", payload["Employee code"])) {
      setAlertMessage("Cannot have duplicate employee codes");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (checkDuplicate(others, "Username", payload.Username)) {
      setAlertMessage("Cannot have duplicate usernames");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setUserDetailsInformation((prev: any) =>
      (prev ?? []).map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editReserveUserDetails(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage("User Details updated successfully");
    setAlertType("success");
    setOpenAlert(true);
  };

  const addReserveUserDetails = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        userDetailsInformation ?? [],
        "Employee code",
        payload["Employee code"],
      )
    ) {
      setAlertMessage("Cannot have duplicate employee codes");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (
      checkDuplicate(userDetailsInformation ?? [], "Username", payload.Username)
    ) {
      setAlertMessage("Cannot have duplicate usernames");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setUserDetailsInformation((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      ),
    );
    setLastUpdatedRecord(payload);
    editReserveUserDetails(null);
    setEditClicked(false);
    setAlertMessage("User Details added successfully");
    setAlertType("success");
    setOpenAlert(true);
  };

  const editReserveUserDetails = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setFirstName("");
      setLastName("");
      setCountryCode("");
      setMobile("");
      setEmailAddress("");
      setEmployeeCode("");
      setUsername("");
      setPassword("");
      setPin("");
      setRetailRole(false);
      setRoleCode("");
      setOutletAccess([]);
      setActive(true);
      clearAllFieldErrors({
        setFirstNameErr,
        setLastNameErr,
        setCountryCodeErr,
        setMobileErr,
        setEmailErr,
        setEmployeeCodeErr,
        setUsernameErr,
        setPasswordErr,
        setPinErr,
        setRoleErr,
      });
      return;
    }
    setEditClicked(true);
    setSelectedId(list.id != null ? String(list.id) : null);
    const legacyName = list?.["Name"] != null ? String(list["Name"]) : "";
    const fn = list?.["First Name"] != null ? String(list["First Name"]) : "";
    const ln = list?.["Last Name"] != null ? String(list["Last Name"]) : "";
    if (fn || ln) {
      setFirstName(fn);
      setLastName(ln);
    } else {
      const parts = legacyName.trim().split(/\s+/);
      setFirstName(parts[0] ?? "");
      setLastName(parts.slice(1).join(" "));
    }
    setCountryCode(
      list?.["Country Code"] != null ? String(list["Country Code"]) : "",
    );
    setMobile(list?.Mobile != null ? String(list.Mobile) : "");
    setEmailAddress(
      list?.["Email address"] != null ? String(list["Email address"]) : "",
    );
    setEmployeeCode(
      list?.["Employee code"] != null ? String(list["Employee code"]) : "",
    );
    setUsername(list?.Username != null ? String(list.Username) : "");
    setPassword(list?.Password != null ? String(list.Password) : "");
    setPin(list?.PIN != null ? String(list.PIN) : "");
    setRetailRole(Boolean(list?.["Retail role"]));
    setRoleCode(list?.["Role Code"] ? String(list["Role Code"]) : "");
    setOutletAccess(
      list?.["Outlet Access"]
        ? String(list["Outlet Access"])
            .split(",")
            .map((part: string) => part.trim())
            .filter(Boolean)
        : [],
    );
    setActive(list?.Active ?? true);
    clearAllFieldErrors({
      setFirstNameErr,
      setLastNameErr,
      setCountryCodeErr,
      setMobileErr,
      setEmailErr,
      setEmployeeCodeErr,
      setUsernameErr,
      setPasswordErr,
      setPinErr,
      setRoleErr,
    });
  };

  const deleteReserveUserDetails = (id: number) => {
    setUserDetailsInformation(deleteItem(userDetailsInformation ?? [], id));
  };

  const firstNameHelperText = firstNameErr
    ? reserveUserDetailsFieldErrors.firstName
    : reserveUserDetailsFieldHelperText.firstName;

  const lastNameHelperText = lastNameErr
    ? reserveUserDetailsFieldErrors.lastName
    : reserveUserDetailsFieldHelperText.lastName;

  const countryCodeHelperText = countryCodeErr
    ? reserveUserDetailsFieldErrors.countryCode
    : reserveUserDetailsFieldHelperText.countryCode;

  const mobileHelperText = mobileErr
    ? reserveUserDetailsFieldErrors.mobile
    : reserveUserDetailsFieldHelperText.mobile;

  const emailHelperText = emailErr
    ? !emailAddress.trim()
      ? reserveUserDetailsFieldErrors.required
      : reserveUserDetailsFieldErrors.emailInvalid
    : reserveUserDetailsFieldHelperText.emailAddress;

  const employeeCodeHelperText = employeeCodeErr
    ? !employeeCode.trim()
      ? reserveUserDetailsFieldErrors.required
      : reserveUserDetailsFieldErrors.employeeCode
    : reserveUserDetailsFieldHelperText.employeeCode;

  const usernameHelperText = usernameErr
    ? !username.trim()
      ? reserveUserDetailsFieldErrors.required
      : reserveUserDetailsFieldErrors.username
    : reserveUserDetailsFieldHelperText.username;

  const passwordHelperText = passwordErr
    ? !password.trim()
      ? reserveUserDetailsFieldErrors.required
      : reserveUserDetailsFieldErrors.password
    : reserveUserDetailsFieldHelperText.password;

  const pinHelperText = pinErr
    ? reserveUserDetailsFieldErrors.pin
    : reserveUserDetailsFieldHelperText.pin;

  const roleHelperText = roleErr
    ? reserveUserDetailsFieldErrors.required
    : reserveUserDetailsFieldHelperText.role;

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack direction="row" width="100%" justifyContent="space-between">
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.userDetails}
              <Tooltip
                title={tooltipComponent(reserveUserDetailsTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editReserveUserDetails(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editReserveUserDetails({
                      ...lastUpdatedRecord,
                      id: undefined,
                      Password: "",
                    });
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Stack flex={1}>
              <FormLabel component="legend">
                First Name
                <Tooltip
                  title={tooltipComponent(reserveUserDetailsTooltip.firstName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                variant="outlined"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setFirstNameErr(false);
                }}
                error={firstNameErr}
                helperText={firstNameHelperText}
              />
            </Stack>
            <Stack flex={1}>
              <FormLabel component="legend">
                Last Name
                <Tooltip
                  title={tooltipComponent(reserveUserDetailsTooltip.lastName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                variant="outlined"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setLastNameErr(false);
                }}
                error={lastNameErr}
                helperText={lastNameHelperText}
              />
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Stack flex={1}>
              <FormLabel component="legend">
                Country Code
                <Tooltip
                  title={tooltipComponent(
                    reserveUserDetailsTooltip.countryCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                variant="outlined"
                placeholder="e.g. +1"
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  setCountryCodeErr(false);
                }}
                error={countryCodeErr}
                helperText={countryCodeHelperText}
              />
            </Stack>
            <Stack flex={1}>
              <FormLabel component="legend">
                Mobile
                <Tooltip
                  title={tooltipComponent(reserveUserDetailsTooltip.mobile)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                variant="outlined"
                placeholder="Mobile"
                type="number"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  setMobileErr(false);
                }}
                error={mobileErr}
                helperText={mobileHelperText}
              />
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend" required>
              Email address
              <Tooltip
                title={tooltipComponent(reserveUserDetailsTooltip.emailAddress)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              variant="outlined"
              type="email"
              placeholder="Email address"
              value={emailAddress}
              onChange={(e) => {
                setEmailAddress(e.target.value);
                setEmailErr(false);
              }}
              error={emailErr}
              helperText={emailHelperText}
              required
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Stack flex={1}>
              <FormLabel component="legend" required>
                Employee code
                <Tooltip
                  title={tooltipComponent(
                    reserveUserDetailsTooltip.employeeCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                variant="outlined"
                placeholder="Employee code"
                value={employeeCode}
                onChange={(e) => {
                  setEmployeeCode(e.target.value);
                  setEmployeeCodeErr(false);
                }}
                error={employeeCodeErr}
                helperText={employeeCodeHelperText}
                required
              />
            </Stack>
            <Stack flex={1}>
              <FormLabel component="legend" required>
                Username
                <Tooltip
                  title={tooltipComponent(reserveUserDetailsTooltip.username)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                variant="outlined"
                placeholder="Username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setUsernameErr(false);
                }}
                error={usernameErr}
                helperText={usernameHelperText}
                autoComplete="username"
                required
              />
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend" required>
              Password
              <Tooltip
                title={tooltipComponent(reserveUserDetailsTooltip.password)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              variant="outlined"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordErr(false);
              }}
              error={passwordErr}
              helperText={passwordHelperText}
              autoComplete="new-password"
              required
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              PIN
              <Tooltip title={tooltipComponent(reserveUserDetailsTooltip.pin)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              variant="outlined"
              type="password"
              placeholder="PIN"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                setPinErr(false);
              }}
              error={pinErr}
              helperText={pinHelperText}
              autoComplete="new-password"
            />
          </Stack>

          <Stack direction="row" gap="2px" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={retailRole}
                  onChange={(_, checked) => setRetailRole(checked)}
                />
              }
              label="Retail role"
            />
            <Tooltip
              title={tooltipComponent(reserveUserDetailsTooltip.retailRole)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Outlet Access
              <Tooltip
                title={tooltipComponent(reserveUserDetailsTooltip.outletAccess)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Stack direction="row" width="100%" gap="5px">
              <Autocomplete
                multiple
                id="reserveUserDetailsOutletAccess"
                disablePortal
                options={removeSampleTestArray(outletAccessList)}
                sx={{ width: "100%" }}
                value={outletAccess}
                disabled={!outletAccessList}
                onChange={(_e, newValue) => {
                  setOutletAccess(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Outlet Access"
                    variant="outlined"
                    placeholder="Outlet Access"
                  />
                )}
              />
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" gap="5px" alignItems="flex-start">
            <FormControl fullWidth required error={roleErr}>
              <InputLabel id="reserve-user-details-role-label" required>
                Role
              </InputLabel>
              <Select
                labelId="reserve-user-details-role-label"
                label="Role"
                value={roleCode}
                onChange={(e: SelectChangeEvent<string>) => {
                  setRoleCode(e.target.value);
                  setRoleErr(false);
                }}
              >
                <MenuItem value="">
                  <em>Select a role</em>
                </MenuItem>
                {rolesList.map((r: any) => (
                  <MenuItem
                    key={String(r["Role Code"])}
                    value={String(r["Role Code"])}
                  >
                    {r["Role Name"] ?? r["Role Code"]}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={roleErr}>{roleHelperText}</FormHelperText>
            </FormControl>
            <Tooltip
              title={tooltipComponent(reserveUserDetailsTooltip.role)}
              sx={{ mt: 1 }}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>

          <Stack direction="row" gap="2px" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            <Tooltip title={tooltipComponent(reserveRowFieldTooltip.active)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId
                  ? updateReserveUserDetails()
                  : addReserveUserDetails()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
              {ReservePropertyEnum.userDetails}
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.userDetails} Available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(userDetailsInformation) &&
              userDetailsInformation.length > 0 ? (
                <DraggableItemList
                  itemData={userDetailsInformation}
                  onOrderUpdate={(order) => setUserDetailsInformation(order)}
                  onEdit={(info) => editReserveUserDetails(info)}
                  onDelete={(id) => deleteReserveUserDetails(id)}
                  code="Employee code"
                  name="Username"
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.userDetails} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveUserDetails;
